import http, { AxiosResponse } from '@libs/http';
import {
  OptionContract,
  OptionPositionsRequestParams,
  Positions,
  Strategies,
  CashSettlementRequest,
  PhysicalSettlementRequest,
  OptionContractsChainRequest,
  UnderlyingAssetIsinsQuery,
  UnderlyingEndDatesRequest,
  OptionContractChain,
  PositionHistoryRequest,
  CashSettlementHistoryRequest,
  PhysicalSettlementHistoryRequest,
  UnderlyingEndDateQuery,
  PositionHistory,
  CashSettlementHistory,
  PhysicalSettlementHistory,
  FrozenHistory,
  OptionStrategiesRequestParams,
  UpdateStrategyRequestParams,
  FreezeUnfreezeQueryParams,
  OptionFrozenHistoryParams,
  StrategyLegs,
  OptionStrategyLegsParams,
} from '@libs/api-reference';

import { TSE_API } from './endpoints';

getOptionContract.url = `${TSE_API}/options/contracts/{isin}` as const;
export function getOptionContract(
  isin: string
): Promise<AxiosResponse<OptionContract>> {
  return http.get<OptionContract>(getOptionContract.url, {
    urlParams: { isin },
    schema: OptionContract,
  });
}

getOptionPositions.url = `${TSE_API}/options/positions` as const;
export function getOptionPositions(
  params: OptionPositionsRequestParams
): Promise<AxiosResponse<Positions>> {
  return http.get<Positions>(getOptionPositions.url, {
    params,
    schema: Positions,
  });
}

getOptionStrategies.url = `${TSE_API}/options/strategies` as const;
export function getOptionStrategies(
  params: OptionStrategiesRequestParams
): Promise<AxiosResponse<Strategies>> {
  return http.get<Strategies>(getOptionStrategies.url, {
    params,
    schema: Strategies,
  });
}

updateOptionStrategy.url = `${TSE_API}/options/strategies` as const;
export function updateOptionStrategy(
  params: UpdateStrategyRequestParams
): Promise<AxiosResponse<{ success: boolean }>> {
  return http.put(updateOptionStrategy.url, params, {
    mock: true,
  });
}

deleteOptionStrategy.url = `${TSE_API}/options/strategies` as const;
export function deleteOptionStrategy(
  isin: string
): Promise<AxiosResponse<{ success: boolean }>> {
  return http.delete(updateOptionStrategy.url, {
    mock: true,
    params: {
      isin,
    },
  });
}

export function createCashSettlement(data: CashSettlementRequest) {
  return http.post(`${TSE_API}/options/cashsettlements`, data);
}

export function createPhysicalSettlement(data: PhysicalSettlementRequest) {
  return http.post(`${TSE_API}/options/physicalsettlement`, data);
}

export function updateCashSettlement(data: CashSettlementRequest) {
  return http.put(`${TSE_API}/options/cashsettlements`, data);
}

export function updatePhysicalSettlement(data: PhysicalSettlementRequest) {
  return http.put(`${TSE_API}/options/physicalsettlement`, data);
}

export function deleteCashSettlement(isin: string) {
  return http.delete(`${TSE_API}/options/cashsettlements`, {
    params: { isin },
  });
}

export function deletePhysicalSettlement(isin: string) {
  return http.delete(`${TSE_API}/options/physicalsettlement`, {
    params: { isin },
  });
}

getPositionsHistory.url = `${TSE_API}/options/positionhistories` as const;
export function getPositionsHistory(params: PositionHistoryRequest) {
  return http.get(getPositionsHistory.url, {
    params,
    schema: PositionHistory,
  });
}

export function getCashSettlementHistory(params: CashSettlementHistoryRequest) {
  return http.get(`${TSE_API}/options/cashsettlementhistories`, {
    params,
    schema: CashSettlementHistory,
  });
}

export function getPhysicalSettlementHistory(
  params: PhysicalSettlementHistoryRequest
) {
  return http.get(`${TSE_API}/options/physicalsettlementhistories`, {
    params,
    schema: PhysicalSettlementHistory,
  });
}

getUnderlyingAssetIsins.url =
  `${TSE_API}/options/contracts/underlyingassetisin` as const;
export function getUnderlyingAssetIsins(): Promise<
  AxiosResponse<UnderlyingAssetIsinsQuery>
> {
  return http.get(getUnderlyingAssetIsins.url, {
    params: {
      pageSize: 100,
    },
    schema: UnderlyingAssetIsinsQuery,
  });
}

getUnderlyingEndDates.url =
  `${TSE_API}/options/contracts/underlyinginstrumentisin/end-dates` as const;
export function getUnderlyingEndDates(
  params: UnderlyingEndDatesRequest
): Promise<AxiosResponse<UnderlyingEndDateQuery>> {
  return http.get(getUnderlyingEndDates.url, {
    params,
    schema: UnderlyingEndDateQuery,
  });
}

getContractsChain.url = `${TSE_API}/options/contracts/chain` as const;
export function getContractsChain(
  params: OptionContractsChainRequest
): Promise<AxiosResponse<OptionContractChain>> {
  return http.get(getContractsChain.url, {
    params,
    schema: OptionContractChain,
  });
}

getFrozenHistory.url = `${TSE_API}/freezerequests` as const;
export function getFrozenHistory(
  params: OptionFrozenHistoryParams
): Promise<AxiosResponse<FrozenHistory>> {
  return http.get(getFrozenHistory.url, {
    schema: FrozenHistory,
    params,
  });
}

freeze.url = `${TSE_API}/freeze/freezerequest` as const;
export function freeze(formModel: FreezeUnfreezeQueryParams) {
  return http.post(freeze.url, formModel);
}

unfreeze.url = `${TSE_API}/freeze/unfreezerequest` as const;
export function unfreeze(formModel: FreezeUnfreezeQueryParams) {
  return http.post(unfreeze.url, formModel);
}

deleteFreeze.url = `${TSE_API}/freeze` as const;
export function deleteFreeze(requestId: number) {
  return http.delete(`${deleteFreeze.url}/${requestId}`);
}

getOptionStrategyLegs.url = `${TSE_API}/options/strategies/legs` as const;
export function getOptionStrategyLegs(
  params: OptionStrategyLegsParams
): Promise<AxiosResponse<StrategyLegs>> {
  return http.get<StrategyLegs>(getOptionStrategyLegs.url, {
    params,
    schema: StrategyLegs,
  });
}
